import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Country, State } from 'country-state-city';
import { Checkbox, Form, Input, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { updateOuDataProperties } from 'redux/slices/getChildOu';
import { SweetSweetAlert } from 'component/sweetalert_components';
import { StyledButton } from 'component/button_components/button_general/ButtonGeneralStyle';
import { useRefreshOuTab } from 'component/table_component/ou_data/refreshOuData';
import {
  FormHeader,
  FormItem,
  FormSubmitButtonConatainer,
  ModalFormContainer,
  RowWrapper,
} from 'component/form_component/style';
import ModalOpenLoader from 'component/loader_component/modalOpenLoader';
import { updateOuInAdminTree } from 'redux/slices/treedata';
import { updateOuInOuAdminTree } from 'redux/slices/ouAdminTree';

export default function UpdateOuProperties({ singleOuData, handleCancel, ...record }) {
  const refreshOuTab = useRefreshOuTab();
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const [form] = Form.useForm();
  const selectedNode = useSelector((state) => state.selectedNode);
  const [states, setStates] = useState([]);
  const [oudeletionCheck, setOudeletionCheck] = useState(record.accidentialDeletion);
  const [loading, setLoading] = useState(false);
  // Set selectedCountryISOCode when the component mounts and country prop has a value
  useEffect(() => {
    if (record.country) {
      const selectedCountryData = Country.getAllCountries().find((countryData) => countryData.name === record.country);
      const selectedCountryISOCode = selectedCountryData ? selectedCountryData.isoCode : '';
      const selectedCountryStates = State.getStatesOfCountry(selectedCountryISOCode);
      setStates(selectedCountryStates);
    }
  }, [record.country]);

  const handleCountryChange = (selectedCountry) => {
    const selectedCountryData = Country.getAllCountries().find((country) => country.name === selectedCountry);

    if (selectedCountryData) {
      const selectedCountryStates = State.getStatesOfCountry(selectedCountryData.isoCode);
      setStates(selectedCountryStates);
    } else {
      setStates([]); // If the selected country is not found, clear the state options
    }
    // Clear the state field value
    form.setFieldsValue({ state: undefined });
  };

  const onFinish = async (values) => {
    values.ouDn = record.dnName;
    values.fqdn = selectedNode.domainName;
    values.accidentialDeletion = oudeletionCheck;
    try {
      setLoading(true);
      const sweetAlert = await dispatch(updateOuDataProperties(values));
      if (sweetAlert.payload && sweetAlert.payload.status === 200) {
        if (record.dnName !== sweetAlert.payload.data.newDnName) {
          if (localStorage.getItem('role') === 'ouadmin') {
            dispatch(
              updateOuInOuAdminTree({
                oldDnName: record.dnName,
                newName: sweetAlert.payload.data.newOuName,
                newDnName: sweetAlert.payload.data.newDnName,
              })
            );
          } else {
            dispatch(
              updateOuInAdminTree({
                oldDnName: record.dnName,
                newName: sweetAlert.payload.data.newOuName,
                newDnName: sweetAlert.payload.data.newDnName,
              })
            );
          }
        }
        SweetSweetAlert({ sweetData: sweetAlert.payload.data.message, icon: 'success', buttonColor: 'green' });
        handleCancel();
        Navigate('/oudata');
      } else {
        SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      }
    } catch (error) {
      SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
    } finally {
      setLoading(false);
    }
    refreshOuTab();
  };

  return (
    <ModalFormContainer className='af-modal-container af-update_ou-modal-container'>
      {loading && <ModalOpenLoader />}
      <FormHeader className='af-form-header af-update_ou-form-header'>Update OU</FormHeader>
      <Form
        className='af-form af-update_ou-form'
        form={form}
        layout='vertical'
        name='register'
        onFinish={onFinish}
        initialValues={{
          description: singleOuData.description ? singleOuData.description : '',
          street: singleOuData.street ? singleOuData.street : '',
          city: singleOuData.city ? singleOuData.city : '',
          state: singleOuData.state ? singleOuData.state : null,
          pincode: singleOuData.pincode ? singleOuData.pincode : '',
          country: singleOuData.country ? singleOuData.country : null,
          ou: singleOuData.name ? singleOuData.name : '',
          fqdn: selectedNode.domainName,
          accidentialDeletion: singleOuData?.accidentialDeletion,
        }}
        scrollToFirstError
        size='large'
      >
        <RowWrapper className='af-form-row-wraper af-update_ou-form-row-wraper af-update_ou-form-row-wraper-wrap1'>
          <FormItem
            className='af-form-item af-update_ou-form-item af-update_ou-form-item-ouname'
            name='ou'
            label='OU Name'
            rules={[
              {
                required: true,
                message: 'Please Enter the Ou',
              },
            ]}
          >
            <Input className='af-form-input af-update_ou-form-input af-update_ou-form-input_ouname' />
          </FormItem>
          <FormItem
            className='af-form-item af-update_ou-form-item af-update_ou-form-item-ouname'
            name='description'
            label='Description'
          >
            <Input className='af-form-input af-update_ou-form-input af-update_ou-form-input_description' />
          </FormItem>
        </RowWrapper>
        <RowWrapper className='af-form-row-wraper af-update_ou-form-row-wraper af-update_ou-form-row-wraper-wrap2'>
          <FormItem
            name='country'
            label='Country'
            className='af-form-item af-update_ou-form-item af-update_ou-form-item-country'
          >
            <Select
              className='af-form-select af-update_ou-form-select af-update_ou-form-select_country'
              showSearch
              placeholder='Select a country'
              optionFilterProp='children'
              onChange={handleCountryChange}
            >
              {Country.getAllCountries().map((country) => (
                <Select.Option
                  className='af-form-select-option af-update_ou-form-select-option af-update_ou-form-select-option_country'
                  key={country.isoCode}
                  value={country.name}
                >
                  {country.name}
                </Select.Option>
              ))}
            </Select>
          </FormItem>

          <FormItem
            name='state'
            label='State'
            className='af-form-item af-update_ou-form-item af-update_ou-form-item-state'
          >
            <Select
              className='af-form-select af-update_ou-form-select af-update_ou-form-select_state'
              showSearch
              placeholder='Select a state'
              optionFilterProp='children'
            >
              {states.map((state) => (
                <Select.Option
                  className='af-form-select-option af-update_ou-form-select-option af-update_ou-form-select-option_state'
                  key={state.isoCode}
                  value={state.name}
                >
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        </RowWrapper>
        <RowWrapper className='af-form-row-wraper af-update_ou-form-row-wraper af-update_ou-form-row-wraper-wrap3'>
          <FormItem
            name='city'
            label='City'
            className='af-form-item af-update_ou-form-item af-update_ou-form-item-city'
          >
            <Input className='af-form-input af-update_ou-form-input af-update_ou-form-input_city' />
          </FormItem>
          <FormItem
            name='street'
            label='Street'
            className='af-form-item af-update_ou-form-item af-update_ou-form-item-street'
          >
            <Input className='af-form-input af-update_ou-form-input af-update_ou-form-input_street' />
          </FormItem>
        </RowWrapper>
        <RowWrapper className='af-form-row-wraper af-update_ou-form-row-wraper af-update_ou-form-row-wraper-wrap4'>
          <FormItem
            name='pincode'
            label='Pincode'
            className='af-form-item af-update_ou-form-item af-update_ou-form-item-postalcode'
          >
            <Input className='af-form-input af-update_ou-form-input af-update_ou-form-input_postalcode' />
          </FormItem>
          <FormItem
            className='af-form-item af-update_ou-form-item af-update_ou-form-item-acc_deletion'
            key='accidentialDeletion'
            name='accidentialDeletion'
            label='Accidental Deletion'
          >
            <Checkbox
              className='af-form-checkbox af-update_ou-form-checkbox af-update_ou-form-checkbox-acc_deletion'
              checked={oudeletionCheck}
              onChange={(e) => setOudeletionCheck(e.target.checked)}
            >
              Protect container from accidental deletion
            </Checkbox>
          </FormItem>
        </RowWrapper>
        <FormSubmitButtonConatainer className='af-form-button-submit-wrapper af-update_ou-form-button-submit-wrapper'>
          <StyledButton
            type='primary'
            htmlType='submit'
            className='af-form-button af-update_ou-form-button af-update_ou-form-button_submit'
          >
            Submit
          </StyledButton>
        </FormSubmitButtonConatainer>
      </Form>
    </ModalFormContainer>
  );
}
